import { isFormulaCell } from './cell'

export type ParsedNumberStatus = 'parsed' | 'blank' | 'invalid' | 'formula-unresolved'
export type ParsedNumberSource = 'number' | 'text' | 'formula-result' | 'blank' | 'unknown'

export interface ParsedBoqNumber {
  value: number | null
  status: ParsedNumberStatus
  source: ParsedNumberSource
}

const blankResult: ParsedBoqNumber = { value: null, status: 'blank', source: 'blank' }

function invalid(status: 'invalid' | 'formula-unresolved' = 'invalid'): ParsedBoqNumber {
  return { value: null, status, source: 'unknown' }
}

function parseTextNumber(raw: string): ParsedBoqNumber {
  let text = raw
    .normalize('NFKC')
    .replace(/[\s\u00a0\u3000]/gu, '')
    .trim()
  if (!text) return blankResult

  const hasOpeningParenthesis = text.startsWith('(')
  const hasClosingParenthesis = text.endsWith(')')
  if (hasOpeningParenthesis !== hasClosingParenthesis) return invalid()
  const parenthesizedNegative = hasOpeningParenthesis && hasClosingParenthesis
  if (parenthesizedNegative) text = text.slice(1, -1)

  text = text
    .replace(/人民币|rmb|cny/giu, '')
    .replace(/[¥￥]/gu, '')
    .replace(/[，,]/gu, '')
    .replace(/元$/u, '')
    .replace(/[−‐‑‒–—]/gu, '-')

  if (!/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/iu.test(text)) return invalid()

  const parsed = Number(text)
  if (!Number.isFinite(parsed)) return invalid()
  const value = parenthesizedNegative ? -Math.abs(parsed) : parsed
  return { value, status: 'parsed', source: 'text' }
}

/** Parses BOQ numeric cells, including ExcelJS formula objects with cached results. */
export function parseBoqNumber(value: unknown): ParsedBoqNumber {
  if (value === null || value === undefined) return blankResult
  if (typeof value === 'number') {
    return Number.isFinite(value) ? { value, status: 'parsed', source: 'number' } : invalid()
  }
  if (typeof value === 'string') return parseTextNumber(value)

  if (isFormulaCell(value)) {
    if (!Object.prototype.hasOwnProperty.call(value, 'result') || value.result === null) {
      return invalid('formula-unresolved')
    }
    const cached = parseBoqNumber(value.result)
    if (cached.status !== 'parsed') return invalid('formula-unresolved')
    return { value: cached.value, status: 'parsed', source: 'formula-result' }
  }

  return invalid()
}

export function parseNumberValue(value: unknown): number | null {
  return parseBoqNumber(value).value
}
